
import { Link, useLocation } from 'react-router-dom';
import { House, Plus, PawPrint, Bell, Grid3X3, LayoutDashboard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CategoriesModal from './CategoriesModal';
import { useIsProvider } from '@/hooks/useIsProvider';

const BottomTabNavigation = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const { data: isProvider } = useIsProvider();

  const isActive = (path: string) => {
    if (path === '/feed') {
      return location.pathname === '/feed';
    }
    if (path === '/my-pets') {
      return location.pathname === '/my-pets' || location.pathname.startsWith('/pet/') || location.pathname.startsWith('/edit-pet/');
    }
    if (path === '/create-post') {
      return location.pathname === '/create-post';
    }
    if (path === '/notifications') {
      return location.pathname === '/notifications';
    }
    return location.pathname === path;
  };

  const tabStyle = "flex flex-col items-center justify-center h-16 flex-1 transition-colors";
  const activeStyle = "text-pet-purple";
  const inactiveStyle = "text-gray-500";

  if (!user) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 safe-area-bottom">
        <div className="flex">
          {/* Feed Tab */}
          <Link to="/feed" className={`${tabStyle} ${isActive('/feed') ? activeStyle : inactiveStyle}`}>
            <House className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">{t('navigation.feed')}</span>
          </Link>

          {/* Pets Tab or Provider Dashboard - Conditional */}
          {isProvider ? (
            <Link to="/provider-dashboard" className={`${tabStyle} ${location.pathname === '/provider-dashboard' ? activeStyle : inactiveStyle}`}>
              <LayoutDashboard className="h-6 w-6 mb-1" />
              <span className="text-xs font-medium">Dashboard</span>
            </Link>
          ) : (
          <Link to="/my-pets" className={`${tabStyle} ${isActive('/my-pets') ? activeStyle : inactiveStyle}`}>
            <PawPrint className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">{t('navigation.myPets')}</span>
          </Link>
          )}

          {/* Create Tab - Hidden for providers */}
          {isProvider ? (
            <div className="flex flex-col items-center justify-center h-16 flex-1 opacity-40 cursor-not-allowed">
              <div className="bg-gray-300 rounded-full p-2 mb-1">
                <Plus className="h-5 w-5 text-gray-500" />
              </div>
              <span className="text-xs font-medium text-gray-500">N/A</span>
            </div>
          ) : (
            <Link to="/create-post" className="flex flex-col items-center justify-center h-16 flex-1">
              <div className="bg-pet-purple rounded-full p-2 mb-1">
                <Plus className="h-5 w-5 text-white" />
              </div>
              <span className="text-xs font-medium text-pet-purple">{t('navigation.create')}</span>
            </Link>
          )}

          {/* Alerts/Notifications Tab */}
          <Link to="/notifications" className={`${tabStyle} ${isActive('/notifications') ? activeStyle : inactiveStyle}`}>
            <Bell className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">{t('navigation.alerts')}</span>
          </Link>

          {/* Categories Tab - Replaces Profile */}
          <button 
            onClick={() => setShowCategoriesModal(true)}
            className={`${tabStyle} ${inactiveStyle}`}
          >
            <Grid3X3 className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">{t('navigation.categories')}</span>
          </button>
        </div>
      </div>

      {/* Categories Modal */}
      <CategoriesModal 
        isOpen={showCategoriesModal}
        onClose={() => setShowCategoriesModal(false)}
      />
    </>
  );
};

export default BottomTabNavigation;
